import {
  ButtonThemes,
  CheckCircleIcon,
  ClockIcon,
  Col,
  Collapse,
  DatePicker,
  EditIcon,
  IconButton,
  Input,
  Modal,
  Row,
  TimesCircleIcon,
  ToastTypes,
} from 'plataforma-fundacao-componentes';

import { AxiosError } from 'axios';
import FaceDown from 'components/Icons/FaceDown';
import LayoutContext from 'components/Layout/LayoutContext';
import { AssemblyModalities } from 'helpers/enums/Assembleia';
import { isDateLessThanToday } from 'helpers/utils/DateUtils';
import { formatData, formatHora } from 'helpers/utils/HTMLUtils';
import { toRequest } from 'helpers/utils/RequestUtils';
import { onlyNumbers } from 'helpers/utils/StringUtils';
import { getAssembleiasAssessorBffEndpoint } from 'library/Apis';
import { AssessorCadastroEventoRoutePath } from 'library/Routes';
import translation from 'locales/translation';
import { ModalProps } from 'plataforma-fundacao-componentes/dist/components/modal/Modal';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import ApiService from 'services/ApiService';
import {
  CloneEventValidation,
  CloneEventValidationType,
} from './CloneEventValidation';
import styles from './styles.module.scss';
import './styles.scss';

export type RequestType = {
  eventId: number;
  quantityAssociates: number | null;
  digitalStartDate: string;
  digitalEndDate: string;
  presentialStartDate?: string;
  presentialEndDate?: string;
};
interface IProps extends ModalProps {
  eventId: number;
  modality: AssemblyModalities;
  onConfirm: (e?: RequestType) => Promise<void>;
}

const prefix = translation.Assessor.Modal.CloneEvent;
const modalReservationPrefix = translation.Assessor.Modal.Reservation;
const homePrefix = translation.Assessor.TelaInicial;

export const ISOToLocaleDateAndHour = (date: string, hour: string) => {
  return date && hour
    ? `${date.split('/').reverse().join('-')}T${hour}:00`
    : '';
};

export const CloneEvent = (props: IProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [cloneError, setCloneError] = useState<boolean>(false);
  const [sent, setSent] = useState<boolean>(false);

  const [digitalInitialDate, setDigitalInitialDate] = useState<string>('');
  const [digitalEndDate, setDigitalEndDate] = useState<string>('');
  const [digitalInitialHour, setDigitalInitialHour] = useState<string>('');
  const [digitalEndHour, setDigitalEndHour] = useState<string>('');
  const [presentialInitialDate, setPresentialInitialDate] =
    useState<string>('');
  const [presentialInitialHour, setPresentialInitialHour] =
    useState<string>('');
  const [presentialEndDate, setPresentialEndDate] = useState<string>('');
  const [presentialEndHour, setPresentialEndHour] = useState<string>('');
  const [quantityAssociates, setQuantityAssociates] = useState<number | null>(
    null
  );
  const [validator, setValidator] = useState<CloneEventValidationType | null>(
    null
  );

  const history = useHistory();
  const { showToast } = useContext(LayoutContext);

  const handleClose = () => {
    props.onClose && props.onClose(props.modalKey);
  };

  useEffect(() => {
    setValidator(
      CloneEventValidation(
        digitalInitialDate,
        digitalEndDate,
        digitalInitialHour,
        digitalEndHour,
        presentialInitialDate,
        presentialEndDate,
        presentialInitialHour,
        presentialEndHour,
        quantityAssociates!,
        props.modality
      )
    );
  }, [
    sent,
    digitalInitialDate,
    digitalEndDate,
    digitalInitialHour,
    digitalEndHour,
    presentialInitialDate,
    presentialInitialHour,
    presentialEndDate,
    presentialEndHour,
    quantityAssociates,
    props.modality,
  ]);

  const request: RequestType = useMemo(() => {
    return {
      eventId: props.eventId,
      digitalStartDate: [
        AssemblyModalities.HIBRIDO_PRESENCIAL_DIGITAL,
      ].includes(props.modality)
        ? ISOToLocaleDateAndHour(presentialInitialDate, presentialInitialHour)
        : ISOToLocaleDateAndHour(digitalInitialDate, digitalInitialHour),
      digitalEndDate: [AssemblyModalities.HIBRIDO_PRESENCIAL_DIGITAL].includes(
        props.modality
      )
        ? ISOToLocaleDateAndHour(presentialEndDate, presentialEndHour)
        : ISOToLocaleDateAndHour(digitalEndDate, digitalEndHour),
      quantityAssociates:
        isDateLessThanToday(
          new Date(
            [AssemblyModalities.HIBRIDO_PRESENCIAL_DIGITAL].includes(
              props.modality
            )
              ? ISOToLocaleDateAndHour(
                  presentialInitialDate,
                  presentialInitialHour
                )
              : ISOToLocaleDateAndHour(digitalInitialDate, digitalInitialHour)
          )
        ) || quantityAssociates === null
          ? null
          : Number(quantityAssociates),
    };
  }, [
    props.eventId,
    props.modality,
    presentialInitialDate,
    presentialInitialHour,
    digitalInitialDate,
    digitalInitialHour,
    presentialEndDate,
    presentialEndHour,
    digitalEndDate,
    digitalEndHour,
    quantityAssociates,
  ]);

  if (
    presentialInitialDate &&
    presentialInitialHour &&
    presentialEndDate &&
    presentialEndHour
  ) {
    Object.assign(request, {
      presentialStartDate: ISOToLocaleDateAndHour(
        digitalInitialDate,
        digitalInitialHour
      ),
      presentialEndDate: ISOToLocaleDateAndHour(digitalEndDate, digitalEndHour),
    });
  }

  const handleSubmit = useCallback(() => {
    if (!sent) setSent(true);
    if (!validator || !validator.allIsValid()) return;
    if (typeof props.onConfirm === 'function') {
      setLoading(true);
      toRequest(ApiService.post, [
        `${getAssembleiasAssessorBffEndpoint()}event/clone`,
        request,
      ])
        .then(({ data }) => {
          if (data.hasError) {
            showToast({
              pauseOnFocusLoss: true,
              theme: ToastTypes.Warning,
              showStatusBar: true,
              timeout: 10000,
              label: homePrefix.modals.cloneModal.warning,
            });
          }
          history.push(
            `${AssessorCadastroEventoRoutePath}${data.event.id}/etapas/dados`
          );

          handleClose();
          setDigitalInitialDate('');
          setDigitalEndDate('');
          setDigitalInitialHour('');
          setDigitalEndHour('');
          setPresentialInitialDate('');
          setPresentialEndDate('');
          setPresentialInitialHour('');
          setPresentialEndHour('');
          setQuantityAssociates(null);
        })
        .catch((error) => {
          if (
            (error as AxiosError<Error>).response?.data.message !==
            'associateQuantity.quantityAssociatesOverflow'
          ) {
            showToast({
              pauseOnFocusLoss: true,
              theme: ToastTypes.Error,
              showStatusBar: true,
              timeout: 4000,
              label:
                translation.Toasts.Errors.Keys[
                  `${
                    (error as AxiosError<Error>).response?.data.message ||
                    (error as AxiosError<Error>).response?.data ||
                    (error as AxiosError<Error>).message
                  }` as keyof typeof translation.Toasts.Errors.Keys
                ] ?? homePrefix.modals.cloneModal.error,
            });
          } else {
            setCloneError(true);
          }
        })
        .finally(() => {
          setLoading(false);
          setSent(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sent, validator, props.onConfirm, request, showToast, history]);

  useEffect(() => {
    if (isDateLessThanToday(new Date(request.digitalStartDate))) {
      setQuantityAssociates(null);
    }
  }, [request.digitalStartDate]);

  return (
    <Modal
      className={cloneError ? styles.root : ''}
      title={prefix.Title}
      preventEscExit={props.preventEscExit || false}
      preventMaskExit={props.preventMaskExit || false}
      modalKey={props.modalKey}
      mobileOn={props.mobileOn}
      onClose={props.onClose}
      size={props.size}
      footer={
        !cloneError
          ? [
              {
                children: prefix.Buttons.Cancel,
                onClick: handleClose,
                theme: ButtonThemes.Secondary,
                leftIcon: <TimesCircleIcon />,
              },
              {
                children: prefix.Buttons.Save,
                onClick: handleSubmit,
                theme: ButtonThemes.Primary,
                leftIcon: <CheckCircleIcon />,
                disabled:
                  loading ||
                  Boolean(sent && validator && !validator.allIsValid()),
                loading: loading,
              },
            ]
          : [
              {
                theme: ButtonThemes.Primary,
                leftIcon: <EditIcon />,
                children: modalReservationPrefix.buttons.voltarEdicao,
                onClick: () => setCloneError(false),
              },
            ]
      }
    >
      <Row>
        <Collapse opened={!cloneError} animateOpacity>
          <Row>
            <Col cols={[12, 12, 6, 6, 6]}>
              <Input
                data-testid={'input-date-event-initial'}
                id={'input-date-event-initial'}
                value={digitalInitialDate}
                onChange={(evt) => {
                  setDigitalInitialDate(formatData(evt.target.value));
                }}
                autoFocus
                rightObject={
                  <DatePicker
                    onChange={(date) => {
                      setDigitalInitialDate(date);
                    }}
                    queryToAppend={'#input-date-event-initial'}
                    initialValue={''}
                    closeOnChange
                  />
                }
                inputMode={'numeric'}
                label={prefix.Inputs.Initial.Date.Label}
                placeholder={prefix.Inputs.Initial.Date.Placeholder}
                disabled={false}
                error={
                  sent &&
                  !!validator &&
                  validator.digitalInitialDateIsValid().error
                }
                helperText={
                  sent && !!validator
                    ? validator.digitalInitialDateIsValid().helperText
                    : ''
                }
              />
            </Col>
            <Col cols={[12, 12, 6, 6, 6]}>
              <Input
                id={'input-hour-event-initial'}
                data-testid={'input-hour-event-initial'}
                value={digitalInitialHour}
                onChange={(evt) => {
                  setDigitalInitialHour(formatHora(evt.target.value));
                }}
                rightObject={
                  <IconButton
                    aria-disabled={true}
                    tabIndex={-1}
                    icon={<ClockIcon />}
                  />
                }
                label={prefix.Inputs.Initial.Hour.Label}
                placeholder={prefix.Inputs.Initial.Hour.Placeholder}
                inputMode={'numeric'}
                maxLength={5}
                error={
                  sent &&
                  !!validator &&
                  validator.digitalInitialHourIsValid().error
                }
                helperText={
                  sent && !!validator
                    ? validator.digitalInitialHourIsValid().helperText
                    : ''
                }
              />
            </Col>
            <Col cols={[12, 12, 6, 6, 6]}>
              <Input
                data-testid={'input-date-event-end'}
                id={'input-date-event-end'}
                value={digitalEndDate}
                onChange={(evt) => {
                  setDigitalEndDate(formatData(evt.target.value));
                }}
                rightObject={
                  <DatePicker
                    onChange={(date) => {
                      setDigitalEndDate(date);
                    }}
                    queryToAppend={'#input-date-event-end'}
                    initialValue={''}
                    closeOnChange
                  />
                }
                inputMode={'numeric'}
                label={prefix.Inputs.End.Date.Label}
                placeholder={prefix.Inputs.End.Date.Placeholder}
                error={
                  sent && !!validator && validator.digitalEndDateIsValid().error
                }
                helperText={
                  sent && !!validator
                    ? validator.digitalEndDateIsValid().helperText
                    : ''
                }
              />
            </Col>
            <Col cols={[12, 12, 6, 6, 6]}>
              <Input
                id={'input-hour-event-end'}
                data-testid={'input-hour-event-end'}
                value={digitalEndHour}
                onChange={(evt) => {
                  setDigitalEndHour(formatHora(evt.target.value));
                }}
                rightObject={
                  <IconButton
                    aria-disabled={true}
                    tabIndex={-1}
                    icon={<ClockIcon />}
                  />
                }
                label={prefix.Inputs.End.Hour.Label}
                placeholder={prefix.Inputs.End.Hour.Placeholder}
                inputMode={'numeric'}
                maxLength={5}
                error={
                  sent && !!validator && validator.digitalEndHourIsValid().error
                }
                helperText={
                  sent && !!validator
                    ? validator.digitalEndHourIsValid().helperText
                    : ''
                }
              />
            </Col>
            <Collapse
              opened={[AssemblyModalities.HIBRIDO_PRESENCIAL_DIGITAL].includes(
                props.modality
              )}
              animateOpacity
            >
              <Row>
                <Col>
                  <h4 className={'nunito'}>{prefix.Semipresential}</h4>
                </Col>
                <Col cols={[12, 12, 6, 6, 6]}>
                  <Input
                    data-testid={'input-date-event-presential-initial'}
                    id={'input-date-event-presential-initial'}
                    value={presentialInitialDate}
                    onChange={(evt) => {
                      setPresentialInitialDate(formatData(evt.target.value));
                    }}
                    rightObject={
                      <DatePicker
                        onChange={(date) => {
                          setPresentialInitialDate(date);
                        }}
                        queryToAppend={'#input-date-event-presential-initial'}
                        initialValue={''}
                        closeOnChange
                        disabled={false}
                      />
                    }
                    inputMode={'numeric'}
                    label={prefix.Inputs.Initial.Date.Label}
                    placeholder={
                      prefix.Inputs.Initial.Date.PlaceholderPresential
                    }
                    disabled={false}
                    error={
                      sent &&
                      !!validator &&
                      validator.presentialInitialDateIsValid().error
                    }
                    helperText={
                      sent && !!validator
                        ? validator.presentialInitialDateIsValid().helperText
                        : ''
                    }
                  />
                </Col>
                <Col cols={[12, 12, 6, 6, 6]}>
                  <Input
                    id={'input-hour-event-presential-initial'}
                    data-testid={'input-hour-event-presential-initial'}
                    value={presentialInitialHour}
                    onChange={(evt) => {
                      setPresentialInitialHour(formatHora(evt.target.value));
                    }}
                    rightObject={<IconButton icon={<ClockIcon />} />}
                    label={prefix.Inputs.Initial.Hour.Label}
                    placeholder={
                      prefix.Inputs.Initial.Hour.PlaceholderPresential
                    }
                    inputMode={'numeric'}
                    maxLength={5}
                    disabled={false}
                    error={
                      sent &&
                      !!validator &&
                      validator.presentialInitialHourIsValid().error
                    }
                    helperText={
                      sent && !!validator
                        ? validator.presentialInitialHourIsValid().helperText
                        : ''
                    }
                  />
                </Col>
                <Col cols={[12, 12, 6, 6, 6]}>
                  <Input
                    data-testid={'input-date-event-presential-end'}
                    id={'input-date-event-presential-end'}
                    value={presentialEndDate}
                    onChange={(evt) => {
                      setPresentialEndDate(formatData(evt.target.value));
                    }}
                    rightObject={
                      <DatePicker
                        onChange={(date) => {
                          setPresentialEndDate(date);
                        }}
                        queryToAppend={'#input-date-event-presential-end'}
                        initialValue={''}
                        closeOnChange
                        disabled={false}
                      />
                    }
                    inputMode={'numeric'}
                    label={prefix.Inputs.End.Date.Label}
                    placeholder={prefix.Inputs.End.Date.PlaceholderPresential}
                    disabled={false}
                    error={
                      sent &&
                      !!validator &&
                      validator.presentialEndDateIsValid().error
                    }
                    helperText={
                      sent && !!validator
                        ? validator.presentialEndDateIsValid().helperText
                        : ''
                    }
                  />
                </Col>
                <Col cols={[12, 12, 6, 6, 6]}>
                  <Input
                    id={'input-hour-event-presential-end'}
                    data-testid={'input-hour-event-presential-end'}
                    value={presentialEndHour}
                    onChange={(evt) => {
                      setPresentialEndHour(formatHora(evt.target.value));
                    }}
                    rightObject={<IconButton icon={<ClockIcon />} />}
                    label={prefix.Inputs.End.Hour.Label}
                    placeholder={prefix.Inputs.End.Hour.PlaceholderPresential}
                    inputMode={'numeric'}
                    maxLength={5}
                    disabled={false}
                    error={
                      sent &&
                      !!validator &&
                      validator.presentialEndHourIsValid().error
                    }
                    helperText={
                      sent && !!validator
                        ? validator.presentialEndHourIsValid().helperText
                        : ''
                    }
                  />
                </Col>
              </Row>
            </Collapse>
            <Collapse
              opened={!isDateLessThanToday(new Date(request.digitalStartDate))}
              animateOpacity
            >
              <Col>
                <Input
                  data-testid={'input-num-associate'}
                  label={prefix.Inputs.QuantityAssociates.Label}
                  placeholder={prefix.Inputs.QuantityAssociates.Placeholder}
                  inputMode={'numeric'}
                  maxLength={5}
                  value={quantityAssociates! ?? null}
                  onChange={(evt) => {
                    if (evt.target.value) {
                      setQuantityAssociates(
                        onlyNumbers(
                          String(evt.target.value)
                        ) as unknown as number
                      );
                    } else {
                      setQuantityAssociates(null);
                    }
                  }}
                  error={
                    sent &&
                    !!validator &&
                    validator.quantityAssociatesIsValid().error
                  }
                  helperText={
                    sent && !!validator
                      ? validator.quantityAssociatesIsValid().helperText
                      : ''
                  }
                  disabled={isDateLessThanToday(
                    new Date(request.digitalStartDate)
                  )}
                />
              </Col>
            </Collapse>
          </Row>
        </Collapse>
        <Collapse opened={cloneError} animateOpacity>
          <Row>
            <Col cols={[2, 2, 2, 2, 2]} noPadding>
              <FaceDown />
            </Col>
            <Col cols={[10, 10, 10, 10, 10]} noPadding>
              <div>
                <h1>{modalReservationPrefix.Error.title}</h1>
                <p>
                  <b>{modalReservationPrefix.Error.body()}</b>
                </p>
                <div className={styles.divider} />
                <span>{modalReservationPrefix.Error.body2()}</span>
              </div>
            </Col>
          </Row>
        </Collapse>
      </Row>
    </Modal>
  );
};
